<template>
    <div
        class="search-component w-full"
        :class="layout === 'stacked' ? 'is-stacked' : 'is-inline'"
    >
        <h3 v-if="title" class="mb-3 px-2 text-center text-lg font-semibold">
            {{ title }}
        </h3>

        <!-- Desktop: segmented search pill -->
        <form class="search-pill" @submit.prevent="onSubmit">
            <!-- Segment 1 — Where to? (attraction / destination) -->
            <div v-if="destinations" class="segment lg:!pl-10">
                <label class="segment-label">{{ destinationLabel }}</label>
                <auto-complete
                    ref="desktopDestRef"
                    v-model="destinationQuery"
                    fluid
                    :delay="200"
                    :suggestions="destinationSuggestions"
                    :option-label="optionLabel"
                    :loading="loadingTours"
                    :placeholder="destinationPlaceholder"
                    complete-on-focus
                    scroll-height="24rem"
                    append-to="body"
                    overlay-class="search-suggestions"
                    @complete="onDestinationSearch"
                    @option-select="onDestinationSelect"
                >
                    <template #option="{ option }">
                        <suggestion-row :option="option" />
                    </template>
                </auto-complete>
            </div>

            <div
                v-if="destinations && experiences"
                class="segment-divider"
            ></div>

            <!-- Segment 2 — Experience (type of experience) -->
            <div v-if="experiences" class="segment">
                <label class="segment-label">{{ experienceLabel }}</label>
                <auto-complete
                    v-model="selectedExperience"
                    fluid
                    :delay="0"
                    :suggestions="experienceSuggestions"
                    option-label="name"
                    :loading="loadingExperiences"
                    :placeholder="experiencePlaceholder"
                    complete-on-focus
                    scroll-height="24rem"
                    append-to="body"
                    overlay-class="experience-overlay"
                    @complete="onExperienceSearch"
                >
                    <template #option="{ option }">
                        <span class="block py-1">{{ option.name }}</span>
                    </template>
                </auto-complete>
            </div>

            <!-- Search button — circular on desktop pill, full-width labelled when stacked -->
            <p-button
                type="submit"
                rounded
                :aria-label="cta"
                icon="pi pi-search"
                size="large"
                :loading="submitting"
                class="search-button-circle"
            />
            <p-button
                type="submit"
                :label="cta"
                icon="pi pi-search"
                fluid
                :loading="submitting"
                class="search-button-full"
            />
        </form>

        <!-- Mobile: collapsed trigger that opens the dialog -->
        <button
            type="button"
            class="search-trigger"
            :class="{ 'is-filled': !!selectedDestination }"
            @click="dialogVisible = true"
        >
            <span class="pi pi-search"></span>
            <span class="search-trigger-label">{{ triggerLabel }}</span>
        </button>

        <!-- Mobile: "Change your search" drawer, slides down from the top -->
        <p-drawer
            v-model:visible="dialogVisible"
            position="top"
            :header="drawerTitle"
            class="search-drawer"
            :style="{ height: 'auto' }"
        >
            <form class="mobile-search" @submit.prevent="onSubmit">
                <div v-if="destinations" class="mobile-field">
                    <span class="pi pi-search field-icon"></span>
                    <auto-complete
                        ref="mobileDestRef"
                        v-model="destinationQuery"
                        fluid
                        :delay="200"
                        :suggestions="destinationSuggestions"
                        :option-label="optionLabel"
                        :loading="loadingTours"
                        :placeholder="destinationPlaceholder"
                        complete-on-focus
                        scroll-height="24rem"
                        @complete="onDestinationSearch"
                        @option-select="onDestinationSelect"
                    >
                        <template #option="{ option }">
                            <suggestion-row :option="option" />
                        </template>
                    </auto-complete>
                </div>

                <div v-if="experiences" class="mobile-field">
                    <span class="pi pi-compass field-icon"></span>
                    <auto-complete
                        v-model="selectedExperience"
                        fluid
                        :delay="0"
                        :suggestions="experienceSuggestions"
                        option-label="name"
                        :loading="loadingExperiences"
                        :placeholder="experiencePlaceholder"
                        complete-on-focus
                        scroll-height="24rem"
                        overlay-class="experience-overlay"
                        @complete="onExperienceSearch"
                    >
                        <template #option="{ option }">
                            <span class="block py-1">{{ option.name }}</span>
                        </template>
                    </auto-complete>
                </div>

                <p-button
                    type="submit"
                    :label="cta"
                    fluid
                    :loading="submitting"
                    class="mobile-search-btn"
                />
            </form>
        </p-drawer>
    </div>
</template>

<script setup lang="ts">
import { computed, h, onMounted, ref } from "vue";
import { AutoComplete, Button as PButton, Drawer as PDrawer } from "primevue";
import {
    AutoCompleteCompleteEvent,
    AutoCompleteOptionSelectEvent,
} from "primevue/autocomplete";
import { router, usePage } from "@inertiajs/vue3";
import { useApiProxy } from "@/Hook/useProxy";
import { Location, Tag, Tour } from "@/types";
import numeral from "numeral";
import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";

const {
    experiences = true,
    destinations = true,
    tag_groups = "",
    destination,
    type,
    title,
    layout = "inline",
    cta = "Search",
    data,
    labels,
    placeholders,
    drawerTitle = "Find your experience",
} = defineProps<{
    experiences?: boolean;
    destinations?: boolean;
    tag_groups?: string | string[];
    // Slug(s) used to *restrict* the destination options (matched against a
    // location's slug / region / country). Does not pre-fill the input.
    destination?: string | string[];
    type?: string | string[];
    title?: string;
    layout?: "inline" | "stacked";
    cta?: string;
    // Pre-filter params forwarded to the tours listing on submit (collected from
    // `data-*` shortcode attributes, mirroring the `<tours>` shortcode).
    data?: Record<string, any>;
    // Field labels / placeholders, overridable via `labels-*` / `placeholders-*`.
    labels?: { destination?: string; experience?: string };
    placeholders?: { destination?: string; experience?: string };
    // Header of the mobile "Change your search" drawer.
    drawerTitle?: string;
}>();

// Resolved field copy (fall back to the defaults when not supplied).
const destinationLabel = computed(() => labels?.destination ?? "Where to?");
const experienceLabel = computed(() => labels?.experience ?? "Experience");
const destinationPlaceholder = computed(
    () => placeholders?.destination ?? "Search for a place or activity",
);
const experiencePlaceholder = computed(
    () => placeholders?.experience ?? "Any experience",
);

// How many destination / tour rows the dropdown shows.
const DEST_LIMIT = 8;
const RESULT_LIMIT = 6;

// --- Normalisation helpers -------------------------------------------------
const toArray = (value?: string | string[]): string[] =>
    value == null ? [] : Array.isArray(value) ? value : [value];

// Fold spelling / punctuation variants so a destination matches however it is
// typed: "St. Lucia" / "st lucia" / "saint lucia", "Turks & Caicos" /
// "turks and caicos", "St. Vincent & the Grenadines" / "st vincent and the...".
const normalizeSearch = (value: string): string =>
    value
        .toLowerCase()
        .replace(/&/g, " and ") // "&" ↔ "and"
        .replace(/\bsaint\b/g, "st") // "saint" → "st" (matches "st" / "st.")
        .replace(/[^a-z0-9]+/g, " ") // drop punctuation: ".", ",", "'", "-", …
        .trim();

// Dropdown rows are a tagged union: a destination, a tour, or the "search for…" action.
type Suggestion =
    | { __type: "destination"; location: Location }
    | { __type: "tour"; tour: Tour }
    | { __type: "search"; query: string };

// --- State -----------------------------------------------------------------
// AutoComplete instances (used to focus the field when a search is attempted
// without a destination).
const desktopDestRef = ref<any>();
const mobileDestRef = ref<any>();

// Mobile "Change your search" dialog visibility.
const dialogVisible = ref(false);

// In-flight navigation state (drives the button spinner).
const submitting = ref(false);

// Destination (attraction/destination filter)
const destinationQuery = ref<any>();
const selectedDestination = ref<Location | null>(null);
const destinationSuggestions = ref<Suggestion[]>([]);

// Experience (type-of-experience filter) — single-select via AutoComplete.
const experienceOptions = ref<Tag[]>([]); // all available tags
const experienceSuggestions = ref<Tag[]>([]); // filtered for the dropdown
const selectedExperience = ref<Tag | string | null>(null); // chosen tag

// Experiences available for the selected destination. `null` means no
// destination is chosen yet, so the full experience list is offered.
const destinationCategorySlugs = ref<Set<string> | null>(null);

const { request, loading: loadingExperiences } = useApiProxy<Tag[]>();
const { request: requestTours, loading: loadingTours } = useApiProxy<Tour[]>();
const { request: requestCategories } = useApiProxy<any[]>();

// Experiences offered for the current destination (all of them until one is set).
const availableExperiences = computed<Tag[]>(() =>
    destinationCategorySlugs.value
        ? experienceOptions.value.filter((t: Tag) =>
              destinationCategorySlugs.value!.has(t.slug),
          )
        : experienceOptions.value,
);

// Label shown on the collapsed mobile trigger.
const triggerLabel = computed(
    () => selectedDestination.value?.name ?? destinationPlaceholder.value,
);

// --- Suggestion rendering --------------------------------------------------
const tourThumb = (t: Tour): string | undefined => {
    const img = t.images?.[t.cover_photo] ?? t.images?.[0];
    return img?.small ?? img?.medium ?? img?.original;
};

const tourMeta = (t: Tour): string => {
    const country = t.location?.[0]?.country;
    const price = t.price ? `From ${numeral(t.price).format("$0,0")}` : "";
    return [country, price].filter(Boolean).join(" | ");
};

// Text written back into the input when an option is chosen.
const optionLabel = (option: Suggestion): string => {
    if (option.__type === "destination") return option.location.name;
    if (option.__type === "tour") return option.tour.name;
    return option.query;
};

// A small functional component renders each row (destination / tour / search),
// reused by both the desktop and mobile AutoComplete.
const SuggestionRow = (props: { option: Suggestion }) => {
    const o = props.option;

    if (o.__type === "destination") {
        return h("div", { class: "flex items-center gap-3 py-1" }, [
            h("span", { class: "pi pi-map-marker text-lg text-primary" }),
            h("div", { class: "text-sm" }, [
                h("div", { class: "font-medium" }, o.location.name),
                h(
                    "div",
                    { class: "text-xs text-gray-400" },
                    o.location.region ?? o.location.country_name,
                ),
            ]),
        ]);
    }

    if (o.__type === "tour") {
        const thumb = tourThumb(o.tour);
        return h("div", { class: "flex items-center gap-3 py-1" }, [
            h(
                "div",
                {
                    class: "h-10 w-12 shrink-0 overflow-hidden rounded-md bg-gray-100",
                },
                thumb
                    ? h("img", {
                          src: thumb,
                          alt: o.tour.name,
                          class: "h-full w-full object-cover",
                          loading: "lazy",
                      })
                    : null,
            ),
            h("div", { class: "min-w-0 text-sm" }, [
                h("div", { class: "truncate font-medium" }, o.tour.name),
                h("div", { class: "text-xs text-gray-400" }, tourMeta(o.tour)),
            ]),
        ]);
    }

    return h("div", { class: "flex items-center gap-3 py-1 text-primary" }, [
        h("span", { class: "pi pi-search" }),
        h("span", { class: "text-sm text-gray-700" }, [
            "Search for ",
            h("strong", o.query),
        ]),
    ]);
};

// --- Destinations: from the global Inertia shared prop, filtered client-side ----
// Optional scope from the `destination` shortcode prop — restricts which
// destinations are offered (matched against the location's own slug, region or
// country slug). Empty = no restriction.
const destinationScope = toArray(destination);
const inDestinationScope = (l: Location): boolean =>
    destinationScope.length === 0 ||
    destinationScope.includes(l.slug) ||
    (l.region_slug != null && destinationScope.includes(l.region_slug)) ||
    (l.country_slug != null && destinationScope.includes(l.country_slug));

const allDestinations = (): Location[] =>
    sortBy(
        uniqBy(
            ((usePage<any>().props.__destinations as Location[]) ?? [])
                .filter((l) => l.slug !== "default")
                .filter(inDestinationScope),
            (l) => l.slug
        ),
        (l) => l.name?.toLowerCase()
    );

// A country is a destination with no region of its own (regions/locations carry
// a `region` / `region_slug`, countries don't).
const isCountry = (l: Location): boolean => !l.region && !l.region_slug;

const matchDestinations = (query: string): Location[] => {
    const all = allDestinations();
    const q = normalizeSearch(query);
    // Initial suggestions on focus: show every country so the user can browse
    // destinations before typing.
    if (!q) return all.filter(isCountry);
    return all
        .filter((l) =>
            [l.name]
                .filter(Boolean)
                .some((field) => normalizeSearch(field!).includes(q)),
        )
        .slice(0, DEST_LIMIT);
};

// Compose the dropdown: matching destinations, then tour results, then the
// "search for…" action when there is a query.
const buildSuggestions = (query: string, tours: Tour[]): Suggestion[] => {
    const rows: Suggestion[] = matchDestinations(query).map((location) => ({
        __type: "destination",
        location,
    }));

    tours
        .slice(0, RESULT_LIMIT)
        .forEach((tour) => rows.push({ __type: "tour", tour }));

    if (query.trim()) rows.push({ __type: "search", query: query.trim() });

    return rows;
};

const fetchTours = (query: string): Promise<Tour[]> =>
    requestTours(
        route("search.tours", { search: query, limit: RESULT_LIMIT }),
        {},
        true,
    )
        .then((tours) => tours ?? [])
        .catch(() => []);

// Guards against out-of-order async responses overwriting newer ones.
let lastQuery = "";

const onDestinationSearch = async (event: AutoCompleteCompleteEvent) => {
    const query = event.query;
    lastQuery = query;

    // Show destinations + the search action immediately…
    destinationSuggestions.value = buildSuggestions(query, []);

    // …then enrich with tour results once the search returns.
    if (query.trim().length >= 2) {
        const tours = await fetchTours(query.trim());
        if (lastQuery === query)
            destinationSuggestions.value = buildSuggestions(query, tours);
    }
};

const onDestinationSelect = (event: AutoCompleteOptionSelectEvent) => {
    const option = event.value as Suggestion;

    if (option.__type === "destination") {
        selectedDestination.value = option.location;
        destinationQuery.value = option.location.name;
        fetchDestinationCategories(option.location);
        return;
    }

    if (option.__type === "tour") {
        router.get(
            route("show.tour", {
                id: option.tour.id,
                tour_slug: option.tour.slug,
            }),
        );
        return;
    }

    // "Search for <query>" — needs a location to land on, so use the selected /
    // top-matched destination; otherwise nudge the user to pick one.
    onTextSearch(option.query);
};

// --- Experiences: proxied admin2 `tags` endpoint (array-capable tag_group) ------
// The chosen experience tag, or null when the field holds a typed string.
const selectedExperienceTag = (): Tag | null =>
    selectedExperience.value && typeof selectedExperience.value !== "string"
        ? selectedExperience.value
        : null;

// Filter the destination's available experiences by the typed query.
const onExperienceSearch = (event: AutoCompleteCompleteEvent) => {
    const q = event.query.trim().toLowerCase();
    experienceSuggestions.value = availableExperiences.value.filter(
        (t: Tag) => !q || t.name.toLowerCase().includes(q),
    );
};

// Restrict the Experience dropdown to the categories the chosen destination
// actually offers (admin2 reports these via the tours meta).
const fetchDestinationCategories = (location: Location) => {
    requestCategories(
        route("search.categories", {
            country: location.country_slug,
            region: location.region_slug,
            location: location.slug,
        }),
        {},
        true,
    )
        .then((cats) => {
            const slugs = new Set((cats ?? []).map((c: any) => c.slug));
            destinationCategorySlugs.value = slugs;

            // Drop a chosen experience the new destination doesn't offer.
            const chosen = selectedExperienceTag();
            if (chosen && !slugs.has(chosen.slug))
                selectedExperience.value = null;

            // Refresh options so an open dropdown reflects the new destination.
            experienceSuggestions.value = availableExperiences.value.slice();
        })
        .catch(console.error);
};

const fetchExperiences = () => {
    if (!experiences) return;
    // Not cached client-side: the experiences list is small and a stale empty
    // cache would otherwise hide newly-available tags.
    request(route("search.tags", { tag_group: tag_groups }))
        .then((tags) => {
            experienceOptions.value = tags ?? [];
            // Seed the dropdown so options are available as soon as it opens
            // (restricted to the destination's categories once one is chosen).
            experienceSuggestions.value = availableExperiences.value.slice();

            // Pre-select an experience passed via the shortcode (`type="slug"`).
            const wanted = toArray(type);
            if (wanted.length)
                selectedExperience.value =
                    experienceOptions.value.find((t: Tag) =>
                        wanted.includes(t.slug),
                    ) ?? null;
        })
        .catch(console.error);
};

// --- Submit / navigation ---------------------------------------------------
// Focus the visible destination field (desktop pill or mobile dialog).
const focusDestination = () => {
    const target = dialogVisible.value
        ? mobileDestRef.value
        : desktopDestRef.value;
    const input: HTMLInputElement | undefined =
        target?.$el?.querySelector?.("input");
    input?.focus();
};

const navigateToListing = (
    location: Location,
    extra: Record<string, any> = {},
) => {
    submitting.value = true;
    dialogVisible.value = false;

    const experienceTag = selectedExperienceTag();

    // Everything being applied beyond the destination itself (experience,
    // free-text search, data-* pre-filters).
    const filters: Record<string, any> = {
        ...(data ?? {}),
        ...(experienceTag ? { tour_category: [experienceTag.slug] } : {}),
        ...extra,
    };

    router.get(
        route("tours", {
            country: location.country_slug,
            region: location.region_slug || undefined,
        }),
        {
            ...filters,
            // Only flag the listing as filtered when something other than the
            // destination is actually being applied.
            ...(Object.keys(filters).length ? { filter: true } : {}),
        },
        {
            onFinish: () => (submitting.value = false),
        },
    );
};

const onTextSearch = (query: string) => {
    const location =
        selectedDestination.value ?? matchDestinations(query)[0] ?? null;

    if (location) {
        navigateToListing(location, { search: query });
        return;
    }

    destinationQuery.value = query;
    focusDestination();
};

const onSubmit = () => {
    const location = selectedDestination.value;

    // A destination is required — the tours listing resolves a location from the
    // country/region path and 404s without one. Nudge the user to pick a place.
    if (!location) {
        focusDestination();
        return;
    }

    navigateToListing(location);
};

// --- Init ------------------------------------------------------------------
onMounted(() => {
    fetchExperiences();
    // `destination` only scopes the options (see `inDestinationScope`); it does
    // not pre-fill the field or pre-select a location.
});
</script>

<style scoped lang="scss">
.search-component {
    margin-inline: auto;
    max-width: 56rem;
    text-align: left;
}

// ---------------------------------------------------------------------------
// Desktop pill (hidden on mobile — the dialog is used there instead)
// ---------------------------------------------------------------------------
.search-pill {
    position: relative;
    z-index: 30; // keep the pill above following page content
    display: none;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #fff;
    border-radius: 1.5rem;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
}

.segment {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
    padding: 0.6rem 1.5rem;
}

.segment-label {
    font-size: 0.8rem;
    font-weight: 700;
    color: #111827;
    text-align: left;
}

.segment-divider {
    display: none;
}

.search-button-circle {
    display: none;
}

.search-button-full {
    width: 100%;
}

// Borderless controls so they blend into the pill (single + multiple AutoComplete).
.search-pill {
    :deep(.p-autocomplete),
    :deep(.p-autocomplete-input-multiple) {
        width: 100%;
        padding: 0;
        gap: 0.35rem;
        background: transparent;
        border: none;
        box-shadow: none;
    }

    :deep(.p-autocomplete-input),
    :deep(.p-inputtext),
    :deep(.p-autocomplete-input-multiple input) {
        padding-inline: 0;
        font-size: 1.1rem;
        line-height: 1.6rem;
        background: transparent;
        border: none;
        box-shadow: none;
    }

    :deep(.p-autocomplete-input::placeholder),
    :deep(.p-autocomplete-input-multiple input::placeholder) {
        font-size: 1.1rem;
        color: #6b7280;
        opacity: 1;
    }

    // Selected experience chips — subtle on-brand tint.
    :deep(.p-autocomplete-chip) {
        background: var(--p-primary-50, #eafaf2);
        color: var(--p-primary-700, #2f855a);
    }
}

// ---------------------------------------------------------------------------
// Mobile trigger (the collapsed search bar)
// ---------------------------------------------------------------------------
.search-trigger {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    width: 100%;
    padding: 1rem 1.5rem;
    background: #fff;
    border: none;
    border-radius: 9999px;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
    cursor: pointer;
    text-align: left;

    .pi {
        font-size: 1.15rem;
        color: #111827;
    }

    .search-trigger-label {
        font-size: 1.05rem;
        color: #6b7280; // placeholder grey
    }

    &.is-filled .search-trigger-label {
        color: #111827;
    }
}

// ---------------------------------------------------------------------------
// Breakpoints: show the pill / hide the trigger on desktop
// ---------------------------------------------------------------------------
@media (min-width: 1024px) {
    .search-pill {
        display: flex;
    }

    .search-trigger {
        display: none;
    }
}

// Inline layout: collapse into a horizontal pill on desktop.
.is-inline {
    @media (min-width: 1024px) {
        .search-pill {
            flex-direction: row;
            align-items: center;
            padding: 0.35rem 0.5rem;
            border-radius: 9999px;
        }

        .segment-divider {
            display: block;
            align-self: stretch;
            width: 1px;
            margin: 0.5rem 0;
            background: var(--color-gray-200, #e5e7eb);
        }

        .search-button-circle {
            display: inline-flex;
            flex: 0 0 auto;
            margin-left: 0.75rem;
            width: 3.75rem !important;
            height: 3.75rem !important;
            padding: 0 !important;

            :deep(.p-button-icon) {
                font-size: 1.15rem;
            }
        }

        .search-button-full {
            display: none;
        }
    }
}
</style>

<!-- Drawer content is teleported to <body>, so it is styled with a non-scoped block. -->
<style lang="scss">
.search-drawer {
    border-bottom-left-radius: 1.25rem;
    border-bottom-right-radius: 1.25rem;
    overflow: hidden;

    .p-drawer-header {
        padding-bottom: 0.5rem;
    }

    .p-drawer-title {
        flex: 1;
        text-align: center;
        font-weight: 700;
    }

    .mobile-search {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
    }

    .mobile-field {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.85rem 1.25rem;
        border: 1px solid #e5e7eb;
        border-radius: 9999px;

        .field-icon {
            font-size: 1.05rem;
            color: #111827;
        }

        .p-autocomplete,
        .p-autocomplete-input-multiple {
            flex: 1;
            width: 100%;
            padding: 0;
            gap: 0.35rem;
            background: transparent;
            border: none;
            box-shadow: none;
        }

        .p-autocomplete-input,
        .p-inputtext,
        .p-autocomplete-input-multiple input {
            padding-inline: 0;
            font-size: 1rem;
            background: transparent;
            border: none;
            box-shadow: none;
        }

        .p-autocomplete-input::placeholder,
        .p-autocomplete-input-multiple input::placeholder {
            color: #6b7280;
            opacity: 1;
        }

        .p-autocomplete-chip {
            background: var(--p-primary-50, #eafaf2);
            color: var(--p-primary-700, #2f855a);
        }
    }

    .mobile-search-btn {
        margin-top: 0.25rem;
        padding-block: 0.85rem;
        font-weight: 700;
    }
}

// Both search dropdowns are teleported to <body> so they're never clipped by an
// ancestor's `overflow: hidden` (e.g. the homepage carousel). Styled globally for
// the same reason — they live outside this component's scoped DOM.
.search-suggestions.p-autocomplete-overlay,
.experience-overlay.p-autocomplete-overlay {
    margin-top: 0.5rem;
    border-radius: 1.25rem;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}

// Experience (tour category) AutoComplete dropdown — roomy, rounded rows.
.experience-overlay.p-autocomplete-overlay {
    .p-autocomplete-list {
        padding: 0.35rem;
    }

    .p-autocomplete-option {
        margin-bottom: 0.15rem;
        padding: 0.7rem 0.85rem; // larger touch targets
        border-radius: 0.85rem;
        font-size: 0.95rem;
        font-weight: 500;
        color: #1f2937;

        &:hover {
            background: var(--p-surface-100, #f3f4f6);
        }
    }
}
</style>
